/**
 * Takvim hücrelerindeki iş ve vizitlerin saat sırasına göre listelenmesi.
 *
 * Saat bilgisi kaynaklara göre farklı alanlarda tutuluyor:
 *   - Vizit → `visitStartTime` (vizit değerlendirmesinde girilen başlangıç
 *     saati), yoksa `startTime` (planlanan saat)
 *   - İş    → `time`
 *
 * Saati girilmemiş kayıtlar listenin sonuna alınır; aksi halde saatsiz bir
 * kayıt saati 00:00 sayılıp en üste çıkardı ve sıralama yanıltıcı olurdu.
 */

/** 'HH:MM' → dakika. Geçersiz/boş değer için null. */
export const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/** Bir vizit kaydının gösterilecek saati. */
export const getVisitTime = (visit) =>
  visit?.visitStartTime || visit?.startTime || '';

/** Bir iş kaydının gösterilecek saati. */
export const getJobTime = (job) => job?.time || '';

const compareByMinutes = (a, b) => {
  // Saati olmayanlar en sona
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

/** Vizitleri saate göre sıralar (kaynak dizi değiştirilmez). */
export const sortVisitsByTime = (visits = []) =>
  [...visits].sort((a, b) =>
    compareByMinutes(timeToMinutes(getVisitTime(a)), timeToMinutes(getVisitTime(b)))
  );

/** İşleri saate göre sıralar (kaynak dizi değiştirilmez). */
export const sortJobsByTime = (jobs = []) =>
  [...jobs].sort((a, b) =>
    compareByMinutes(timeToMinutes(getJobTime(a)), timeToMinutes(getJobTime(b)))
  );

/**
 * Vizit ve işleri tek bir saat sıralı listede birleştirir.
 * Her eleman `{ kind: 'visit' | 'job', item, minutes }` biçimindedir; render
 * tarafı `kind` alanına bakarak ilgili bileşeni çizer.
 *
 * Aynı saatte çakışanlarda vizitler önce gelir (operasyonda vizit önceliklidir).
 */
export const mergeByTime = (visits = [], jobs = []) => {
  const entries = [
    ...visits.map((item) => ({
      kind: 'visit',
      item,
      minutes: timeToMinutes(getVisitTime(item)),
    })),
    ...jobs.map((item) => ({
      kind: 'job',
      item,
      minutes: timeToMinutes(getJobTime(item)),
    })),
  ];

  return entries.sort((a, b) => {
    const byTime = compareByMinutes(a.minutes, b.minutes);
    if (byTime !== 0) return byTime;
    if (a.kind === b.kind) return 0;
    return a.kind === 'visit' ? -1 : 1;
  });
};

/** Saat etiketi; saat girilmemişse boş string döner. */
export const formatTimeLabel = (value) => {
  const minutes = timeToMinutes(value);
  if (minutes === null) return '';
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mins = String(minutes % 60).padStart(2, '0');
  return `${hours}:${mins}`;
};
